import type {
    SmartDimensionCandidate,
    SmartDimensionPickItem,
    SmartDimensionVec2,
} from './SketchSmartDimensionTypes';

/** 推断策略输入。 */
export interface SmartDimensionInferenceInput {
    /** 有序选择项。 */
    readonly picks: readonly SmartDimensionPickItem[];
    /** 指针位置（草图局部 2D，米）。 */
    readonly pointer: SmartDimensionVec2;
}

/**
 * 推断策略：把有序选择 + 指针位置翻译为纯数据候选。
 *
 * 约定：组合合法但几何退化（零长线等）时返回 null，不抛错；
 * 策略不创建约束、不写渲染对象。
 */
export type SmartDimensionInferenceStrategy = (
    input: SmartDimensionInferenceInput,
) => SmartDimensionCandidate | null;

/**
 * 智能尺寸推断策略注册表。
 *
 * 设计说明：
 * 1. 按选择种类签名（种类字典序组合，如 `line+point`）注册策略，满足 OCP：
 *    阶段 6 中心线/样条/象限点策略按新签名注册，不修改既有策略；
 * 2. 签名对种类排序（组合无序），策略内部按 picks 原序消费（选择顺序有语义）。
 */
export class SketchDimensionInferenceRegistry {
    /** 签名到策略映射。 */
    private readonly strategies = new Map<string, SmartDimensionInferenceStrategy>();

    /**
     * 计算选择组合签名。
     *
     * @param picks 有序选择项。
     * @returns 种类字典序签名。
     */
    public static signatureOf(picks: readonly SmartDimensionPickItem[]): string {
        return picks
            .map((pick) => pick.itemKind)
            .sort()
            .join('+');
    }

    /**
     * 注册策略。
     *
     * @param signature 选择组合签名。
     * @param strategy 推断策略。
     * @throws 当同一选择组合签名已注册策略时抛出错误。
     */
    public register(signature: string, strategy: SmartDimensionInferenceStrategy): void {
        if (this.strategies.has(signature)) {
            throw new Error(`推断策略已注册：${signature}`);
        }
        this.strategies.set(signature, strategy);
    }

    /**
     * 按当前选择解析候选。
     *
     * @param input 推断输入。
     * @returns 候选；组合未注册或几何退化时返回 null。
     */
    public resolve(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
        if (input.picks.length === 0) {
            return null;
        }

        const strategy = this.strategies.get(SketchDimensionInferenceRegistry.signatureOf(input.picks));

        return strategy ? strategy(input) : null;
    }

    /**
     * 判断选择组合是否可能通过继续选择形成候选。
     *
     * @param picks 当前有序选择项。
     * @returns 是否存在以当前签名为前缀的注册策略。
     */
    public hasPotentialCombination(picks: readonly SmartDimensionPickItem[]): boolean {
        if (picks.length === 0) {
            return true;
        }

        const kinds = picks.map((pick) => pick.itemKind).sort();

        return [...this.strategies.keys()].some((signature) => {
            const parts = signature.split('+');

            if (parts.length < kinds.length) {
                return false;
            }

            // 组合无序：当前种类多重集必须是注册签名的子多重集。
            const remaining = [...parts];

            return kinds.every((kind) => {
                const index = remaining.indexOf(kind);

                if (index < 0) {
                    return false;
                }
                remaining.splice(index, 1);

                return true;
            });
        });
    }
}
